import { useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

/**
 * Hook to monitor and maintain session health
 * Automatically detects and recovers from session loss
 */
export function useSessionHealth() {
  const { user } = useAuth()
  const lastCheckRef = useRef<Date>(new Date())
  const recoveryAttemptsRef = useRef(0)
  
  useEffect(() => {
    if (!user) return
    
    // Check session health every 30 seconds
    const healthCheckInterval = setInterval(async () => {
      try {
        const now = new Date()
        const timeSinceLastCheck = now.getTime() - lastCheckRef.current.getTime()
        
        // Skip if we just checked
        if (timeSinceLastCheck < 20000) return
        
        lastCheckRef.current = now
        
        // Check if we still have a valid session
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error || !session) {
          console.warn('🔐 Session health check failed:', error?.message || 'No session')
          
          // Try to recover the session
          if (recoveryAttemptsRef.current < 3) {
            recoveryAttemptsRef.current++
            console.log('🔐 Attempting session recovery...', recoveryAttemptsRef.current)
            
            // Try to refresh the session
            const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession()
            
            if (newSession && !refreshError) {
              console.log('🔐 Session recovered successfully')
              recoveryAttemptsRef.current = 0
            } else {
              console.error('🔐 Session recovery failed:', refreshError)
              
              // Check if we have cached credentials
              const cachedAuth = localStorage.getItem('facility-pro-auth')
              if (cachedAuth) {
                try {
                  const parsed = JSON.parse(cachedAuth)
                  if (parsed.currentSession?.refresh_token) {
                    console.log('🔐 Attempting recovery from cache...')
                    const { error: setError } = await supabase.auth.setSession({
                      access_token: parsed.currentSession.access_token,
                      refresh_token: parsed.currentSession.refresh_token
                    })
                    
                    if (!setError) {
                      console.log('🔐 Session recovered from cache')
                      recoveryAttemptsRef.current = 0
                    }
                  }
                } catch (e) {
                  console.error('🔐 Cache recovery failed:', e)
                }
              }
            }
          } else {
            console.error('🔐 Max recovery attempts reached, user needs to log in again')
            // Reset counter for next time
            recoveryAttemptsRef.current = 0
          }
        } else {
          // Session is healthy, reset recovery attempts
          if (recoveryAttemptsRef.current > 0) {
            console.log('🔐 Session health restored')
            recoveryAttemptsRef.current = 0
          }
        }
      } catch (error) {
        console.error('🔐 Session health check error:', error)
      }
    }, 30000) // Check every 30 seconds
    
    // Clean up on unmount
    return () => {
      clearInterval(healthCheckInterval)
    }
  }, [user])
}